import {
  getToolHandler,
  isToolSupported,
} from 'app/pages/loanApplication/tabs/aiChat/toolHandlers';
import _ from 'lodash';

import {
  AiChatSignalRMessageType,
  getToolConfirmationLevel,
  ToolConfirmationLevel,
  ToolExecutionStatus,
} from 'constants/aiChat';
import { jsonParse, jsonStringify } from 'utils/json';

import {
  addToolExecutionResultAction,
  clearPendingToolExecutionAction,
  setPendingToolExecutionAction,
} from 'store/aiChat/actions';

const parseToolArguments = toolArguments => {
  if (_.isNil(toolArguments)) {
    return {};
  }

  if (_.isObject(toolArguments)) {
    return toolArguments;
  }

  try {
    return jsonParse(toolArguments);
  } catch {
    return {};
  }
};

const buildResultPayload = result => {
  if (_.isNil(result?.result)) {
    return null;
  }

  try {
    return jsonStringify(result.result);
  } catch {
    return null;
  }
};

class FrontendToolHandler {
  #connection;
  #logger;
  #dispatch;
  #getState;
  #context;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
    this.#context = {};
  }

  connect({ dispatch, getState }) {
    this.#dispatch = dispatch;
    this.#getState = getState;
  }

  disconnect() {
    this.#dispatch = null;
    this.#getState = null;
    this.#context = {};
  }

  setContext(newContext) {
    this.#context = { ...this.#context, ...newContext };
  }

  get context() {
    return this.#context;
  }

  async handleToolExecutionRequest(event) {
    if (!this.#dispatch) {
      return null;
    }

    const { sessionId, messageId, toolCallId, toolName, friendlyName, toolArguments } = event ?? {};

    if (_.isNil(toolCallId) || _.isNil(toolName)) {
      this.#logger?.warn?.('[FrontendToolHandler] Missing toolCallId or toolName', event);

      return null;
    }

    const parameters = parseToolArguments(toolArguments);

    if (!isToolSupported(toolName)) {
      const result = {
        sessionId,
        messageId,
        toolCallId,
        toolName,
        success: false,
        status: ToolExecutionStatus.FAILED,
        error: `Tool "${toolName}" is not supported on the frontend`,
      };

      this.#sendToolResult(result);
      this.#recordResult(result);

      return result;
    }

    const confirmationLevel = getToolConfirmationLevel(toolName);

    if (_.isEqual(confirmationLevel, ToolConfirmationLevel.CONFIRM)) {
      const pendingExecution = {
        sessionId,
        messageId,
        toolCallId,
        toolName,
        friendlyName: friendlyName ?? null,
        parameters,
        status: ToolExecutionStatus.CONFIRMING,
        createdAt: new Date().toISOString(),
      };

      this.#dispatch(setPendingToolExecutionAction(pendingExecution));

      return pendingExecution;
    }

    return this.#executeTool({ sessionId, messageId, toolCallId, toolName, parameters });
  }

  async confirmToolExecution() {
    const pendingExecution = this.#readPending();

    if (!pendingExecution || !this.#dispatch) {
      return null;
    }

    this.#dispatch(clearPendingToolExecutionAction());

    return this.#executeTool(pendingExecution);
  }

  async rejectToolExecution(reason) {
    const pendingExecution = this.#readPending();

    if (!pendingExecution || !this.#dispatch) {
      return null;
    }

    this.#dispatch(clearPendingToolExecutionAction());

    const result = {
      sessionId: pendingExecution.sessionId,
      messageId: pendingExecution.messageId,
      toolCallId: pendingExecution.toolCallId,
      toolName: pendingExecution.toolName,
      success: false,
      status: ToolExecutionStatus.CANCELLED,
      error: reason || 'User rejected the action',
    };

    this.#sendToolResult(result);
    this.#recordResult(result);

    return result;
  }

  async #executeTool(request) {
    const { toolName, parameters, sessionId, messageId, toolCallId } = request;

    const handler = getToolHandler(toolName);

    if (!handler) {
      const result = {
        sessionId,
        messageId,
        toolCallId,
        toolName,
        success: false,
        status: ToolExecutionStatus.FAILED,
        error: `No handler found for tool "${toolName}"`,
      };

      this.#sendToolResult(result);
      this.#recordResult(result);

      return result;
    }

    try {
      const handlerResult = await handler({
        toolName,
        parameters,
        context: this.#context,
      });

      const fullResult = {
        sessionId,
        messageId,
        toolCallId,
        toolName,
        ...handlerResult,
      };

      this.#sendToolResult(fullResult);
      this.#recordResult(fullResult);

      return fullResult;
    } catch (error) {
      const result = {
        sessionId,
        messageId,
        toolCallId,
        toolName,
        success: false,
        status: ToolExecutionStatus.FAILED,
        error: error?.message || 'Tool execution failed',
      };

      this.#sendToolResult(result);
      this.#recordResult(result);

      return result;
    }
  }

  #readPending() {
    if (!this.#getState) {
      return null;
    }

    const state = this.#getState();

    return state?.aiChat?.pendingToolExecution ?? null;
  }

  #sendToolResult(result) {
    if (!this.#connection?.isConnected) {
      this.#logger?.warn?.('[FrontendToolHandler] Cannot send tool result — connection inactive');

      return;
    }

    const { sessionId, toolCallId, success, error } = result;
    const { companyId } = this.#context;

    if (_.isNil(sessionId) || _.isNil(companyId) || _.isNil(toolCallId)) {
      this.#logger?.warn?.(
        '[FrontendToolHandler] Missing sessionId/companyId/toolCallId — skipping send',
      );

      return;
    }

    const resultPayload = buildResultPayload(result);
    const errorMessage = error ?? null;

    this.#connection
      .invoke(
        AiChatSignalRMessageType.SEND_TOOL_RESULT,
        sessionId,
        companyId,
        toolCallId,
        !!success,
        resultPayload,
        errorMessage,
      )
      .catch(invokeError => {
        this.#logger?.error?.('[FrontendToolHandler] Failed to send tool result', invokeError);
      });
  }

  #recordResult(result) {
    if (!this.#dispatch) {
      return;
    }

    this.#dispatch(
      addToolExecutionResultAction({
        ...result,
        completedAt: new Date().toISOString(),
      }),
    );
  }
}

export { FrontendToolHandler };

export default FrontendToolHandler;
